import cn from 'classnames';
import { ReactNode, useCallback } from 'react';

import { Pencil, CheckCircleFilled } from '@zooplus/icons';
import { P1, P2, H2, FlatBox, Anchor } from '@zooplus/ui-components';

import {
  EditProps,
  SetEditContainerProps,
} from '../preview-containers-section/preview-containers-section';

import usePageTracking from '@hooks/use-adobe-tracking/use-page-tracking';
import {
  PAGE_PATH,
  TARGET_ACTION_ID,
  TARGET_COMPONENT_NAME,
  TRACKING_DATA_EVENT,
} from '@utilities/constants';

import styles from './container-component.module.scss';

type ContainerComponentProps = {
  containerNumber: number;
  children: ReactNode;
  containerName?: string;
  dataTestId?: string;
  isContainerActive?: boolean;
  isContainerComplete?: boolean;
  editContainer?: SetEditContainerProps;
  evaluateContainerName?: (value: string | string[]) => string;
  edit?: EditProps | boolean;
  activeContainerIndex?: number;
  isLastContainer?: boolean;
};

const ContainerComponent = ({
  containerName,
  containerNumber,
  dataTestId,
  children,
  isContainerActive,
  isContainerComplete,
  editContainer,
  evaluateContainerName,
  edit,
  activeContainerIndex,
  isLastContainer,
}: ContainerComponentProps) => {
  const { trackEventUsingTrackerListener } = usePageTracking();
  const containerStatus = isContainerActive
    ? styles.containerHeadingWrapper__active
    : isContainerComplete
      ? styles.containerHeadingWrapper__complete
      : styles.containerHeadingWrapper__inactive;

  const isEditObject = typeof edit === 'object';
  const customEditName =
    isEditObject && edit?.name
      ? typeof edit?.name === 'string'
        ? edit?.name
        : edit?.name()
      : undefined;

  const editLabel = customEditName
    ? evaluateContainerName?.(customEditName)
    : evaluateContainerName?.('checkout.preview.edit');

  const editContainerLink = (
    <span className={styles.editLink}>
      <P1 className={styles.editLabel} bold>
        {editLabel}
      </P1>
      <Pencil width={18} height={18} className={styles.editPencil} />
    </span>
  );

  const handleEditBasketClick = useCallback(() => {
    trackEventUsingTrackerListener(
      TRACKING_DATA_EVENT.CLICK,
      TARGET_ACTION_ID.EDIT_BASKET,
      TARGET_COMPONENT_NAME.PREVIEW_CONTAINER,
    );
  }, [trackEventUsingTrackerListener]);

  const handleEditLinkClick = useCallback(
    (sectionName?: string) => {
      if (!sectionName) {
        return;
      }

      const sectionActionMap = {
        email: TARGET_ACTION_ID.EDIT_EMAIL_SECTION,
        payment: TARGET_ACTION_ID.EDIT_PAYMENT_SECTION,
      };

      const targetActionId = Object.entries(sectionActionMap).find(([key]) =>
        sectionName.toLowerCase().includes(key),
      )?.[1];

      trackEventUsingTrackerListener(
        TRACKING_DATA_EVENT.CLICK,
        targetActionId,
        TARGET_COMPONENT_NAME.PREVIEW_CONTAINER,
      );
    },
    [trackEventUsingTrackerListener],
  );

  const getCustomEditName = () =>
    customEditName ? (
      <Anchor data-testid="edit-basket-link" href={PAGE_PATH.CART} onClick={handleEditBasketClick}>
        {editContainerLink}
      </Anchor>
    ) : (
      <Anchor onClick={() => handleEditLinkClick(containerName)}>{editContainerLink}</Anchor>
    );

  const shouldGetEditButton =
    edit &&
    ((isContainerActive && isEditObject && edit?.shouldDisplayAlways) ||
      (!isContainerActive && isContainerComplete));

  const shouldApplyDivider =
    isContainerComplete && !isLastContainer && activeContainerIndex !== containerNumber + 1;

  const isContainerInactive = !(isContainerActive || isContainerComplete);
  const shouldShowNumberContainer =
    !isContainerComplete || (isContainerActive && isContainerComplete);

  return (
    <FlatBox
      rounded
      variant={isContainerActive ? 'neutral' : 'white'}
      className={cn(
        styles.containerComponent,
        isContainerInactive && 'pp-mt-2xl pp-px-l pp-py-0',
        isContainerActive &&
          `${styles.containerComponent_removeDivider} ${styles.containerComponent_active}`,
        shouldApplyDivider && styles.containerComponent_divider,
      )}
      data-testid={dataTestId}>
      <header
        className={cn(styles.containerHeadingWrapper, containerStatus)}
        data-testid={`${isContainerActive ? 'active' : isContainerComplete ? 'complete' : 'inActive'}-container-heading`}>
        {shouldShowNumberContainer ? (
          <div className={styles.containerNumber}>
            <P2>{containerNumber + 1}</P2>
          </div>
        ) : (
          <CheckCircleFilled />
        )}
        <H2
          className={cn(
            styles.containerHeading,
            containerNumber === 3 && styles.lastContainerHeading,
          )}
          data-testid={`${dataTestId}-heading`}>
          {containerName}
        </H2>
        {shouldGetEditButton ? (
          <div
            tabIndex={0}
            className={styles.editButtonWrapper}
            data-testid="edit-button"
            onClick={editContainer}
            onKeyDown={editContainer}>
            {getCustomEditName()}
          </div>
        ) : null}
      </header>
      <section>{children}</section>
    </FlatBox>
  );
};

export default ContainerComponent;
